import type { ShellConfig } from './models'
import type { SettingsInfo } from '../common'

import type { RegisterApplicationConfig } from 'single-spa'
import { GTC_GUIDE_ROUTE, TERMS_OF_SERVICE_ROUTE, LANDING_ROUTE } from '../common/routes'

import { getShellApiInstance } from '../common/shell-api-helpers'
import { getWindowOptions } from '../services/container/windows-management'
import {
  createBannerNotificationElement,
  createChameleonLauncherScreenElement,
  createChameleonThemeProviderElement,
  createExperienceContentElement,
  createGotoRootElement,
  createMainElement,
  createModalNotificationElement,
  createNavigationElement,
  createDialogContainer,
  createSnackbarNotificationElement,
  createSpecificAreaElement,
  createTopBarContainer,
  getLocationPathname,
  createInspectorPanelContainer,
  createCriticalBannerContainerElement,
} from '../common/dom-helpers'
import { BASEURL_AUTH, userReportUUID } from '../environments'
import { setupUserReport } from '../packages/user-report/index'

import { getLaunchDarklyFlag } from '../services/feature-flags'
import { LaunchDarklyVariations } from '../services/feature-flags/models'
import {
  CHAMELEON_THEME_PROVIDER,
  REBRANDING_2021_THEME,
  CHAMELEON_LAUNCH_SCREEN,
  GOTO_ROOT_SELECTOR,
} from '../common/container'
import { createSecureIframe } from '../services/auth/serverIsLoggedIn'
import { gotoSettingPageExperienceConfig, generalSettingsExperienceConfigs } from '../experiences'
import { gotoExperienceConfigToSingleSpaConfig } from '../experiences/helpers'

/**
 * The general settings will be a static list of GotoExperienceConfig which we transform into the proper format that will be used to register them as experiences.
 */
export function createGeneralSettingsExperiences(): readonly RegisterApplicationConfig[] {
  return Object.values(generalSettingsExperienceConfigs).map(config => gotoExperienceConfigToSingleSpaConfig(config))
}

/**
 * Create the setting experience.
 * This is the settings main page that own all the settings
 * @param SettingsInfo[]
 * @returns
 */
export const createGotoSettingsExperience = (settingsInfo: readonly SettingsInfo[]): RegisterApplicationConfig => ({
  ...gotoSettingPageExperienceConfig,
  customProps: { settingLinks: settingsInfo },
})

export const goToConnectExperienceRoutes = [
  '/dashboard',
  '/domain',
  '/jc-jiveweb',
  '/settings',
  '/shared',
  GTC_GUIDE_ROUTE,
] as const

export const routesWithoutSidebar = [
  '/onboard-calendar',
  '/onboard-gotoconnect',
  '/tos',
  '/calendar-connected',
  TERMS_OF_SERVICE_ROUTE,
  LANDING_ROUTE,
  BASEURL_AUTH.redirect,
] as const

export const routesWithoutTopbar = [LANDING_ROUTE]

export const canShowSideBar = (config?: ShellConfig): boolean => {
  const options = getWindowOptions()
  const isAuthenticated = !!getShellApiInstance()?.user.key
  const onRouteWithoutSidebar = routesWithoutSidebar.some(route => getLocationPathname().startsWith(route))
  const isLocationWithoutSidebar = onRouteWithoutSidebar || getLocationPathname() === '/'

  // No sidebar - if `shell-navigation-bar` is `false` or if user is not authenticated or if current path in `routesWithoutSidebar`
  if (!isAuthenticated || isLocationWithoutSidebar) {
    return false
  }

  return !!options.showSideBar && !config?.hideSidebar
}

export const canShowTopBar = (config?: ShellConfig) => {
  const onRouteWithoutTopbar = routesWithoutTopbar.some(route => getLocationPathname() === route)
  const isAuthenticated = !!getShellApiInstance()?.user.key
  if (!isAuthenticated || onRouteWithoutTopbar) {
    return false
  }
  const options = getWindowOptions()
  return !!options.showTopBar && !config?.hideTopbar
}

/**
 * Creates the HTML layout for the Shell
 */
export function scaffoldHTML() {
  if (document.querySelector(GOTO_ROOT_SELECTOR)) {
    return
  }
  const appRoot = createGotoRootElement()
  const topbarContainer = createTopBarContainer()
  const inspectorPanelContainer = createInspectorPanelContainer()
  const main = createMainElement()
  const content = createExperienceContentElement()
  content.appendChild(createChameleonLauncherScreenElement())
  const navigation = createNavigationElement()

  const criticalBannerContainer = createCriticalBannerContainerElement()
  const bannerNotification = createBannerNotificationElement()
  const snackbarNotification = createSnackbarNotificationElement()
  const modalNotification = createModalNotificationElement()
  const dialogContainer = createDialogContainer()

  const chameleonThemeProvider = createChameleonThemeProviderElement()
  const specificArea = createSpecificAreaElement()
  chameleonThemeProvider.setAttribute('skin', 'gotoapp')

  const mainChildren = [content]
  mainChildren.forEach(element => {
    main.appendChild(element)
  })

  const appRootChildren = [
    criticalBannerContainer,
    bannerNotification,
    topbarContainer,
    navigation,
    specificArea,
    main,
    inspectorPanelContainer,
  ]

  appRootChildren
    .filter((value): value is HTMLElement => Boolean(value))
    .forEach(element => {
      appRoot.appendChild(element)
    })

  const appItems = [appRoot, modalNotification, dialogContainer]

  appItems.forEach(element => chameleonThemeProvider.append(element))
  document.body.append(chameleonThemeProvider)
  document.body.append(createSecureIframe())
  document.body.querySelector('.goto-container')?.append(snackbarNotification)

  // Feedback form setup https://www.userreport.com/
  const { mount } = setupUserReport(window)
  mount(userReportUUID)
}

export function allowSwitchToDarkmode(): void {
  const chameleonThemeProvider = document.querySelector(CHAMELEON_THEME_PROVIDER)
  if (getLaunchDarklyFlag(LaunchDarklyVariations.SHELL_ENABLE_DARKMODE) && chameleonThemeProvider) {
    // The 'automatic' prop will set the theme to light or dark depending on what the OS is set to
    chameleonThemeProvider.setAttribute('automatic', 'true')
  }
}

export function applyRebranding2021Theme(): void {
  const chameleonThemeProvider = document.querySelector(CHAMELEON_THEME_PROVIDER)
  if (chameleonThemeProvider) {
    chameleonThemeProvider.setAttribute(REBRANDING_2021_THEME, 'true')
  }
}

export function resetChameleonLaunchScreenStyle(): void {
  const chameleonLaunchScreen = document.querySelector(CHAMELEON_LAUNCH_SCREEN)
  chameleonLaunchScreen?.setAttribute('style', '')
}
